import { useOneButtonStore } from '../../../../../store/oneButtonStore';

export type TrackLetter = 'A' | 'B' | 'C';

export type TrackStatus = {
    track: TrackLetter;
    letterClass: string;
    progressClass: string;
    bannerMessage: string | null;
    bannerType: 'amber' | 'red' | null;
    flaggedCount: number;
    color: string;
};

export type ChecklistViewState = {
    checklistName: string | null;
    productTab: string;
    expandedSectionIds: any[];
};

export type FlaggedItem = {
    questionId: any;
    displayOrder: string | number;
    questionText: string;
    answerText: string;
    checklistName: string;
    productTab: string;
    sectionId: any;
    color?: string;
    tone?: 'red' | 'amber';
};

const DEFAULT_PRODUCTS: { [key: string]: boolean } = {
    Solar: false,
    Roof: false,
    Battery: false,
    RRR: false,
    RR: false,
};

const EMPTY_TRACK: TrackStatus = {
    track: 'A',
    letterClass: 'text-success',
    progressClass: 'bg-success',
    bannerMessage: null,
    bannerType: null,
    flaggedCount: 0,
    color: '#16a34a',
};

/**
 * Stateful port of Angular `SalesrepChecklistComponent` (mutates nested API objects, then notifies React).
 */
export class SalesRepChecklistEngine {
    salesrepChecklistDetails: any[] = [];
    opportunityId: string | null = null;
    selectedTabIndex = 0;
    activeTab: any = null;
    tobarDetails: any = null;
    unsavedChangesCount = 0;
    saveDisabled = true;
    productSelectionValidationMessage: string | null = null;
    selectedProducts: { [key: string]: boolean } = { ...DEFAULT_PRODUCTS };
    activeProductTab = 'Solar';
    checklistDynamicTemplates: { [product: string]: any[] } = {};
    showReopenModal = false;
    sectionToReopen: any = null;
    hasAutoAdvancedToSubmission = false;
    readonly sharedTabKey = 'Shared';
    /** SaveChecklistResponses rejects answer options without a ModifiedUserFullName. */
    userFullName = '';
    private previousParentAnswersMap = new Map<any, string[]>();
    private sectionAutoCloseTimers = new Map<any, ReturnType<typeof setTimeout>>();

    constructor(private readonly notify: () => void) {}

    dispose(): void {
        this.sectionAutoCloseTimers.forEach((timer) => clearTimeout(timer));
        this.sectionAutoCloseTimers.clear();
    }

    hydrateFromApi(data: any, viewStateToRestore?: ChecklistViewState | null): void {
        this.previousParentAnswersMap.clear();
        this.salesrepChecklistDetails = data?.checkListResponse || [];
        this.tobarDetails = data?.context;
        if (this.salesrepChecklistDetails.length === 0) {
            this.notify();
            return;
        }
        this.applySavedProductSelection();
        this.applyPrefilledQuestionValues();
        this.captureInitialResponses?.();
        this.updateSaveButtonState();
        if (viewStateToRestore && this.restoreChecklistViewState(viewStateToRestore)) {
            this.notify();
            return;
        }
        if (this.isContractCreationComplete()) {
            this.hasAutoAdvancedToSubmission = true;
            const submissionIndex = this.getChecklistIndexByName('Contract Submission');
            this.selectTab(submissionIndex >= 0 ? submissionIndex : 0, false);
        } else {
            this.selectTab(0, false);
        }
        this.notify();
    }

    captureChecklistViewState(): ChecklistViewState {
        const currentSections = this.checklistDynamicTemplates[this.activeProductTab] || [];
        return {
            checklistName: this.getCurrentChecklistName(),
            productTab: this.activeProductTab,
            expandedSectionIds: currentSections
                .filter((section: any) => section?.expanded)
                .map((section: any) => this.getSectionIdentity(section))
                .filter((sectionId: any) => sectionId !== undefined && sectionId !== null),
        };
    }

    getQuestionsForSave(): any[] {
        const questionMap = new Map<any, any>();
        const addQuestion = (question: any): void => {
            if (!question) return;
            const questionKey =
                question.questionId !== undefined && question.questionId !== null ? question.questionId : question;
            questionMap.set(questionKey, question);
        };

        this.getUnsavedAnsweredQuestions().forEach((question: any) => addQuestion(question));

        (this.salesrepChecklistDetails || []).forEach((checklist: any) => {
            const sectionZeroQuestion = this.getSectionZeroQuestion(checklist.checklistName);
            if (sectionZeroQuestion && (this.isQuestionAnswered(sectionZeroQuestion) || sectionZeroQuestion.isUnsaved)) {
                addQuestion(sectionZeroQuestion);
            }
            this.getChecklistSectionsForSave(checklist).forEach((section: any) => {
                this.getAllActiveQuestionsForSection(section).forEach((question: any) => {
                    if (this.isQuestionAnswered(question) && this.isPrefilledQuestionForSave(question)) {
                        addQuestion(question);
                    }
                });
            });
        });

        return Array.from(questionMap.values());
    }

    buildSavePayload(): { answers: any[]; allActiveQuestionIds: any[]; unsavedQuestions: any[] } {
        const questionsForSave = this.getQuestionsForSave();
        const unsavedQuestions = questionsForSave.filter((q: any) => q.isUnsaved);
        const answers = questionsForSave
            .map((q: any) => {
                const val = q.currentResponse !== undefined && q.currentResponse !== null ? q.currentResponse : q.answer;
                return {
                    questionId: q.questionId,
                    answers: this.buildAnswerItems(q, val),
                    question: q,
                };
            })
            .filter((item) => item.answers && (item.answers.length > 0 || this.isSectionZeroQuestion(item.question)))
            .map(({ question: _question, ...item }) => item);

        return {
            answers,
            allActiveQuestionIds: this.getActiveQuestionIdsOnSave(),
            unsavedQuestions,
        };
    }

    markSaved(unsavedQuestions: any[]): void {
        unsavedQuestions.forEach((q: any) => {
            const val =
                q.currentResponse !== undefined && q.currentResponse !== null
                    ? q.currentResponse
                    : q.answer;
            q.initialResponse = val !== undefined && val !== null ? JSON.parse(JSON.stringify(val)) : undefined;
            q.isUnsaved = false;
        });
        this.updateSaveButtonState();
        this.notify();
    }

    selectTab(index: number, shouldNotify = true): void {
        this.selectedTabIndex = index;
        this.onTabChange(index, shouldNotify);
    }

    onTabChange(index: number, shouldNotify = true): void {
        const checklists = this.salesrepChecklistDetails || [];
        if (index < checklists.length) {
            const currentChecklist = checklists[index];
            this.activeTab = currentChecklist.checklistId;
            this.checklistDynamicTemplates = this.groupSectionsByProduct(currentChecklist.checklistName);
            this.initializeTemplateSectionExpansion();
            this.setDefaultProductTabForChecklist(currentChecklist.checklistName);
            this.applyPrefilledQuestionValues();
        } else {
            this.activeTab = 'summary';
        }
        if (shouldNotify) this.notify();
    }

    isActiveChecklist(checklistName: string): boolean {
        if (this.activeTab === 'summary') return false;
        const checklist = this.salesrepChecklistDetails?.find((item) => item.checklistName === checklistName);
        return checklist ? this.activeTab === checklist.checklistId : false;
    }

    getChecklistIndexByName(checklistName: string): number {
        return (this.salesrepChecklistDetails || []).findIndex((item) => item.checklistName === checklistName);
    }

    getChecklistDetailsByName(checklistName: string): any {
        return this.salesrepChecklistDetails?.find((item) => item.checklistName === checklistName);
    }

    getSectionZeroQuestion(checklistName: string): any {
        const checklist = this.getChecklistDetailsByName(checklistName);
        const section = checklist?.sections?.find((s: any) => s.sectionId === 0);
        if (section?.questions?.length) return section.questions[0];
        return null;
    }

    isContractCreationComplete(): boolean {
        return this.isChecklistComplete('Contract Creation');
    }

    isChecklistComplete(checklistName: string): boolean {
        const checklist = this.getChecklistDetailsByName(checklistName);
        if (!checklist) return false;
        if (checklistName === 'Contract Creation' && !this.isAnyProductSelected()) return false;

        const templates = this.groupSectionsByProduct(checklistName);
        const tabs =
            checklistName === 'Contract Submission'
                ? this.getChecklistSubTabs(checklistName, templates)
                : this.getActiveProducts();
        if (!tabs.length) return false;

        for (const tabKey of tabs) {
            for (const section of templates[tabKey] || []) {
                if (section.sectionId === 0) continue;
                if (this.getAllActiveQuestionsForSection(section).some((q: any) => !this.isQuestionAnswered(q))) {
                    return false;
                }
            }
        }
        return true;
    }

    getChecklistCompletionStats(checklistName: string): { answered: number; total: number; percentage: number } {
        const templates = this.groupSectionsByProduct(checklistName);
        const tabs =
            checklistName === 'Contract Submission'
                ? this.getChecklistSubTabs(checklistName, templates)
                : this.getActiveProducts();
        let answered = 0;
        let total = 0;
        tabs.forEach((tabKey: string) => {
            (templates[tabKey] || []).forEach((section: any) => {
                if (section.sectionId === 0) return;
                answered += this.getSectionAnsweredCount(section);
                total += this.getSectionTotalCount(section);
            });
        });
        return { answered, total, percentage: total > 0 ? Math.round((answered / total) * 100) : 0 };
    }

    getOverallStats(): { answered: number; total: number; percentage: number } {
        let answered = 0;
        let total = 0;
        (this.salesrepChecklistDetails || []).forEach((checklist: any) => {
            if (!checklist?.checklistName) return;
            const stats = this.getChecklistCompletionStats(checklist.checklistName);
            answered += stats.answered;
            total += stats.total;
        });
        return { answered, total, percentage: total > 0 ? Math.round((answered / total) * 100) : 0 };
    }

    getChecklistSubTabs(
        checklistName: string,
        templates: { [product: string]: any[] } = this.checklistDynamicTemplates,
    ): string[] {
        const selectedProducts = this.getActiveProducts();
        if (checklistName === 'Contract Submission') {
            const tabs: string[] = [];
            if ((templates[this.sharedTabKey] || []).length > 0) tabs.push(this.sharedTabKey);
            selectedProducts.forEach((product: string) => {
                if ((templates[product] || []).length > 0) tabs.push(product);
            });
            return tabs;
        }
        return selectedProducts.filter((product: string) => (templates[product] || []).length > 0);
    }

    getChecklistTabStatusLabel(checklistName: string): string {
        if (this.isChecklistComplete(checklistName)) return 'Completed';
        const trackStatus = this.getChecklistTrackStatus(checklistName);
        if (trackStatus.track !== 'A') return `Track ${trackStatus.track}`;
        return 'In Progress';
    }

    getChecklistTabPillClass(checklistName: string): string {
        if (this.isChecklistComplete(checklistName)) return 'tab-pill-summary';
        const track = this.getChecklistTrackStatus(checklistName).track;
        if (track === 'B') return 'tab-pill-warning';
        if (track === 'C') return 'tab-pill-danger';
        return 'tab-pill-in-progress';
    }

    groupSectionsByProduct(checklistName: string): { [product: string]: any[] } {
        const sections = this.getChecklistDetailsByName(checklistName)?.sections || [];
        return sections.reduce((result: any, section: any) => {
            if (section.sectionId === 0 || !this.isSectionVisible(section)) return result;
            const applicableProducts = this.getSectionApplicableProducts(section);
            const productsToMap = applicableProducts.length ? applicableProducts : this.getActiveProducts();
            productsToMap.forEach((product: string) => {
                const key = String(product).trim().toLowerCase() === 'shared' ? this.sharedTabKey : product;
                if (!result[key]) result[key] = [];
                result[key].push(section);
            });
            return result;
        }, {});
    }

    isQuestionRequired(question: any): boolean {
        return !this.isTextareaQuestion(question) && (question?.required || question?.isMandatory);
    }

    getSortedQuestions(questions: any[], productTabContext?: string): any[] {
        if (!questions) return [];
        return questions
            .filter((q: any) => this.isQuestionVisible(q, productTabContext))
            .sort((a: any, b: any) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
    }

    isQuestionVisible(question: any, productTabContext?: string): boolean {
        if (!question) return false;
        if (!this.checkFinancePartnerCondition(question, productTabContext)) return false;
        if (!this.checkFiltersCondition(question)) return false;
        if (!this.checkParentAnswerOptionIdsCondition(question)) return false;
        return true;
    }

    getSavedResponseAnswers(response: any): any[] {
        if (!response || typeof response !== 'object' || !Array.isArray(response.answers)) return [];
        return response.answers.filter((answer: any) => answer !== undefined && answer !== null);
    }

    getResponseValues(response: any): any[] {
        if (response === undefined || response === null || response === '') return [];
        if (Array.isArray(response)) {
            return response.reduce((values: any[], item: any) => values.concat(this.getResponseValues(item)), []);
        }
        if (typeof response === 'object') {
            const savedAnswers = this.getSavedResponseAnswers(response);
            if (savedAnswers.length > 0) {
                return savedAnswers.reduce((values: any[], answer: any) => values.concat(this.getResponseValues(answer)), []);
            }
            const value =
                response.responseValue !== undefined && response.responseValue !== null
                    ? response.responseValue
                    : response.value !== undefined && response.value !== null
                      ? response.value
                      : response.answerText !== undefined && response.answerText !== null
                        ? response.answerText
                        : response.answerOptionId;
            return value !== undefined && value !== null && value !== '' ? [value] : [];
        }
        return [response];
    }

    getQuestionResponseValues(question: any): any[] {
        if (!question) return [];
        const response =
            question.currentResponse !== undefined && question.currentResponse !== null
                ? question.currentResponse
                : question.answer;
        const values = this.getResponseValues(response);
        if (question.isMultiSelect && values.length === 1 && typeof values[0] === 'string') {
            return values[0]
                .split(',')
                .map((value: string) => value.trim())
                .filter(Boolean);
        }
        return values;
    }

    getOptionCompareValues(opt: any): any[] {
        if (!opt) return [];
        if (typeof opt === 'string') return [opt];
        return [opt.value, opt.answerText, opt.label, opt.answerOptionId].filter(
            (value: any) => value !== undefined && value !== null && value !== '',
        );
    }

    valuesMatch(left: any, right: any): boolean {
        if (left === right) return true;
        if (left === null || left === undefined || right === null || right === undefined) return false;
        return String(left).trim().toLowerCase() === String(right).trim().toLowerCase();
    }

    responseMatchesOption(response: any, opt: any): boolean {
        const responseValues = this.getResponseValues(response);
        const optionValues = this.getOptionCompareValues(opt);
        return responseValues.some((responseValue: any) =>
            optionValues.some((optionValue: any) => this.valuesMatch(responseValue, optionValue)),
        );
    }

    getSelectedOptions(question: any): any[] {
        if (!question) return [];
        const responseValues = this.getQuestionResponseValues(question);
        if (responseValues.length === 0) return [];
        const options = question.answerOptions || question.options || [];
        return options.filter((opt: any) =>
            responseValues.some((responseValue: any) => this.responseMatchesOption(responseValue, opt)),
        );
    }

    getActiveChildQuestions(question: any, productTabContext?: string): any[] {
        if (!question) return [];
        const selectedOpts = this.getSelectedOptions(question);
        let childQs: any[] = [];
        selectedOpts.forEach((opt: any) => {
            if (opt && opt.childQuestions && opt.childQuestions.length > 0) {
                childQs = childQs.concat(opt.childQuestions);
            }
        });
        if (childQs.length > 0) return this.getSortedQuestions(childQs, productTabContext);
        if (question.childQuestions && question.childQuestions.length > 0 && this.isQuestionAnswered(question)) {
            return this.getSortedQuestions(question.childQuestions, productTabContext);
        }
        return [];
    }

    getAllActiveQuestionsForQuestion(question: any, productTabContext?: string): any[] {
        if (!question) return [];
        let result = [question];
        for (const childQ of this.getActiveChildQuestions(question, productTabContext)) {
            result = result.concat(this.getAllActiveQuestionsForQuestion(childQ, productTabContext));
        }
        return result;
    }

    getAllActiveQuestionsForSection(section: any, productTabContext?: string): any[] {
        if (!section || (!section.questions && !section.questionsList)) return [];
        const questions = section.questions || section.questionsList || [];
        let allActive: any[] = [];
        for (const q of this.getSortedQuestions(questions, productTabContext)) {
            allActive = allActive.concat(this.getAllActiveQuestionsForQuestion(q, productTabContext));
        }
        return allActive;
    }

    isQuestionAnswered(q: any): boolean {
        if (!q) return false;
        if (this.isTextareaQuestion(q)) return true;
        return this.getQuestionResponseValues(q).length > 0;
    }

    isQuestionUnsaved(q: any): boolean {
        return !!q?.isUnsaved;
    }

    onQuestionResponseChange(question: any): void {
        if (!question) return;
        const currentVal =
            question.currentResponse !== undefined && question.currentResponse !== null
                ? question.currentResponse
                : question.answer;
        const initialVal = question.initialResponse;
        const isDifferent = !this.areResponseValuesEqual(currentVal, initialVal);
        question.isUnsaved = isDifferent;
        question.userOverridden = true;
        question.DateTimeStamp = new Date().toISOString();
        question.ModifiedUserFullName = this.userFullName;
        this.applyPrefilledQuestionValues();
        this.updateSaveButtonState();
        const section = this.findSectionForQuestion(question);
        if (section) this.handleSectionAutoCollapse(section);
        this.evaluateContractCreationCompletion();
        this.notify();
    }

    onSectionHeaderToggle(section: any, expanded: boolean): void {
        if (!section) return;
        const secKey = section.sectionId || section;
        this.clearSectionTimer(secKey);
        if (expanded && this.isSectionComplete(section) && !section.expanded) {
            this.sectionToReopen = section;
            this.showReopenModal = true;
            this.notify();
            return;
        }
        section.expanded = expanded;
        this.notify();
    }

    confirmReopenSection(): void {
        if (this.sectionToReopen) this.sectionToReopen.expanded = true;
        this.showReopenModal = false;
        this.sectionToReopen = null;
        this.notify();
    }

    cancelReopenSection(): void {
        if (this.sectionToReopen) this.sectionToReopen.expanded = false;
        this.showReopenModal = false;
        this.sectionToReopen = null;
        this.notify();
    }

    toggleProduct(product: string, checklistName: string): void {
        if (!product) return;
        const validationMessage = this.getProductSelectionValidationMessage(product);
        if (validationMessage) {
            this.productSelectionValidationMessage = validationMessage;
            this.notify();
            return;
        }
        const isSelecting = !this.selectedProducts[product];
        this.selectedProducts[product] = isSelecting;
        this.productSelectionValidationMessage = this.isAnyProductSelected()
            ? null
            : 'At least one product must be selected.';
        this.checklistDynamicTemplates = this.groupSectionsByProduct(checklistName);
        this.initializeTemplateSectionExpansion(isSelecting ? product : undefined);
        this.syncProductSelectorQuestionSelection(checklistName);
        if (isSelecting) this.activeProductTab = product;
        if (!this.selectedProducts[this.activeProductTab]) {
            const remaining = this.getActiveProducts();
            if (remaining.length > 0) this.activeProductTab = remaining[0];
        }
        this.evaluateContractCreationCompletion();
        this.updateSaveButtonState();
        this.notify();
    }

    onNavigateToQuestion(item: FlaggedItem): void {
        if (!item) return;
        const targetChecklistName = item.checklistName || 'Contract Creation';
        const tabIndex = (this.salesrepChecklistDetails || []).findIndex(
            (c: any) => c.checklistName === targetChecklistName,
        );
        this.selectTab(tabIndex !== -1 ? tabIndex : 0, false);
        if (item.productTab) this.selectProductTab(item.productTab, false);
        const templates = this.checklistDynamicTemplates[this.activeProductTab] || [];
        const targetSec = templates.find((sec: any) => sec.sectionId === item.sectionId);
        if (targetSec) targetSec.expanded = true;
        this.notify();
        window.setTimeout(() => {
            const qElement = document.getElementById('question-' + item.questionId);
            if (qElement) {
                qElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                qElement.classList.add('highlight-question-flash');
                window.setTimeout(() => qElement.classList.remove('highlight-question-flash'), 2500);
            }
        }, 150);
    }

    getActiveProducts(): string[] {
        return Object.keys(this.selectedProducts).filter((key) => this.selectedProducts[key]);
    }

    isAnyProductSelected(): boolean {
        return this.getActiveProducts().length > 0;
    }

    selectProductTab(product: string, shouldNotify = true): void {
        this.activeProductTab = product;
        if (shouldNotify) this.notify();
    }

    getSectionAnsweredCount(section: any, productTabContext?: string): number {
        return this.getAllActiveQuestionsForSection(section, productTabContext).filter((q) => this.isQuestionAnswered(q)).length;
    }

    getSectionTotalCount(section: any, productTabContext?: string): number {
        return this.getAllActiveQuestionsForSection(section, productTabContext).length;
    }

    getProductAnsweredCount(product: string): number {
        return (this.checklistDynamicTemplates[product] || []).reduce(
            (sum: number, sec: any) => sum + this.getSectionAnsweredCount(sec, product),
            0,
        );
    }

    getProductTotalCount(product: string): number {
        return (this.checklistDynamicTemplates[product] || []).reduce(
            (sum: number, sec: any) => sum + this.getSectionTotalCount(sec, product),
            0,
        );
    }

    isSectionComplete(section: any, productTabContext?: string): boolean {
        const total = this.getSectionTotalCount(section, productTabContext);
        return total > 0 && this.getSectionAnsweredCount(section, productTabContext) === total;
    }

    getUnsavedAnsweredQuestions(): any[] {
        const result: any[] = [];
        (this.salesrepChecklistDetails || []).forEach((checklist: any) => {
            const sectionZeroQuestion = this.getSectionZeroQuestion(checklist.checklistName);
            if (
                sectionZeroQuestion?.isUnsaved &&
                this.isAnyProductSelected()
            ) {
                result.push(sectionZeroQuestion);
            }
            this.getChecklistSectionsForSave(checklist).forEach((sec: any) => {
                const questions = sec.questions || sec.questionsList || [];
                this.getSortedQuestions(questions).forEach((q: any) => {
                    this.getAllActiveQuestionsForQuestion(q).forEach((aq: any) => {
                        if (aq?.isUnsaved) result.push(aq);
                    });
                });
            });
        });
        return result;
    }

    buildAnswerItems(q: any, val: any): any[] {
        const items: any[] = [];
        const selectedOpts = this.getSelectedOptions(q);
        const savedAnswers = this.getSavedResponseAnswers(val);
        const modifiedUserFullName = String(q?.ModifiedUserFullName || '').trim() || this.userFullName;
        const dateTimeStamp = q?.DateTimeStamp || new Date().toISOString();
        const buildItem = (answerOptionId: any, answerText: string, responseValue: any) => ({
            answerOptionId,
            answerText,
            responseValue,
            DateTimeStamp: dateTimeStamp,
            ModifiedUserFullName: modifiedUserFullName,
        });

        if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
            items.push(buildItem(0, '', ''));
            return items;
        }

        if (savedAnswers.length > 0) {
            savedAnswers.forEach((answer: any) => {
                const selected = selectedOpts.find((opt: any) => this.responseMatchesOption(answer, opt));
                const answerText = selected
                    ? selected.answerText || selected.label || selected.value || ''
                    : answer.answerText || answer.label || answer.responseValue || answer.value || '';
                const answerOptionId = selected
                    ? selected.answerOptionId !== undefined
                        ? selected.answerOptionId
                        : null
                    : answer.answerOptionId !== undefined
                      ? answer.answerOptionId
                      : null;
                items.push(
                    buildItem(
                        answerOptionId,
                        answerText,
                        answer.responseValue !== undefined
                            ? answer.responseValue
                            : answer.value || answer.answerText || answerOptionId,
                    ),
                );
            });
            return items;
        }

        if (Array.isArray(val)) {
            val.forEach((item: any) => {
                const selected = selectedOpts.find((opt: any) => {
                    const optVal = opt.value !== undefined ? opt.value : opt.answerText || opt.label;
                    const itemVal =
                        typeof item === 'object'
                            ? item.value !== undefined
                                ? item.value
                                : item.answerText || item.label
                            : item;
                    return optVal === itemVal || opt.answerOptionId === itemVal || String(optVal) === String(itemVal);
                });
                const answerText = selected
                    ? selected.answerText || selected.label || selected.value || ''
                    : typeof item === 'object'
                        ? item.answerText || item.label || item.value || ''
                        : String(item);
                const answerOptionId = selected
                    ? selected.answerOptionId !== undefined
                        ? selected.answerOptionId
                        : null
                    : null;
                items.push(buildItem(answerOptionId, answerText, item));
            });
            return items;
        }

        let answerText = '';
        let answerOptionId: any = null;
        if (selectedOpts.length > 0) {
            const selected = selectedOpts[0];
            answerText = selected.answerText || selected.label || selected.value || '';
            answerOptionId = selected.answerOptionId !== undefined ? selected.answerOptionId : null;
        } else if (typeof val === 'object') {
            answerText = val.answerText || val.label || val.value || '';
            answerOptionId = val.answerOptionId !== undefined ? val.answerOptionId : null;
        } else {
            answerText = String(val);
        }
        items.push(buildItem(answerOptionId, answerText, val));
        return items;
    }

    getQuestionSelectedTone(q: any): 'red' | 'amber' | 'green' | null {
        if (!q) return null;
        const selectedOpts = this.getSelectedOptions(q);
        if (!selectedOpts.length) return null;
        let hasAmber = false;
        let hasGreen = false;
        for (const selectedOpt of selectedOpts) {
            if (!selectedOpt?.tone) continue;
            const toneStr = String(selectedOpt.tone.tone || '').toLowerCase();
            if (toneStr.includes('red') || selectedOpt.tone.isCritical) return 'red';
            if (toneStr.includes('amber')) hasAmber = true;
            if (toneStr.includes('green')) hasGreen = true;
        }
        if (hasAmber) return 'amber';
        if (hasGreen) return 'green';
        return null;
    }

    getQuestionSelectedToneColor(q: any): string | null {
        if (!q) return null;
        const selectedOpts = this.getSelectedOptions(q);
        if (!selectedOpts.length) return null;
        let amberColor: string | null = null;
        let greenColor: string | null = null;
        for (const opt of selectedOpts) {
            if (!opt?.tone) continue;
            const toneStr = String(opt.tone.tone || '').toLowerCase();
            const color = opt.tone.color || null;
            if (toneStr.includes('red') || opt.tone.isCritical) return color || '#dc2626';
            if (toneStr.includes('amber') && !amberColor) amberColor = color || '#d97706';
            if (toneStr.includes('green') && !greenColor) greenColor = color || '#16a34a';
        }
        return amberColor || greenColor;
    }

    getSectionToneColor(section: any): string | null {
        const activeQuestions = this.getAllActiveQuestionsForSection(section);
        let amberColor: string | null = null;
        let greenColor: string | null = null;
        for (const q of activeQuestions) {
            for (const opt of this.getSelectedOptions(q)) {
                if (!opt?.tone) continue;
                const toneStr = String(opt.tone.tone || '').toLowerCase();
                const color = opt.tone.color || null;
                if (toneStr.includes('red') || opt.tone.isCritical) return color || '#dc2626';
                if (toneStr.includes('amber') && !amberColor) amberColor = color || '#d97706';
                if (toneStr.includes('green') && !greenColor) greenColor = color || '#16a34a';
            }
        }
        if (amberColor) return amberColor;
        if (this.isSectionComplete(section)) return greenColor || '#16a34a';
        return null;
    }

    getSectionTone(section: any): 'red' | 'amber' | 'green' | 'default' {
        let hasAmber = false;
        for (const q of this.getAllActiveQuestionsForSection(section)) {
            const tone = this.getQuestionSelectedTone(q);
            if (tone === 'red') return 'red';
            if (tone === 'amber') hasAmber = true;
        }
        if (hasAmber) return 'amber';
        if (this.isSectionComplete(section)) return 'green';
        return 'default';
    }

    shouldShowCriticalTag(section: any): boolean {
        const tone = this.getSectionTone(section);
        return tone === 'red' || tone === 'amber';
    }

    getChecklistTrackStatus(checklistName: string): TrackStatus {
        if (!checklistName) return { ...EMPTY_TRACK };
        let redCount = 0;
        let amberCount = 0;
        let criticalColor: string | null = null;
        let warningColor: string | null = null;
        let greenColor: string | null = null;
        const processedQuestionIds = new Set<any>();
        const templates = this.groupSectionsByProduct(checklistName);
        const tabs =
            checklistName === 'Contract Submission'
                ? this.getChecklistSubTabs(checklistName, templates)
                : this.getActiveProducts();

        tabs.forEach((product: string) => {
            (templates[product] || []).forEach((sec: any) => {
                if (!sec || sec.sectionId === 0) return;
                this.getAllActiveQuestionsForSection(sec, product).forEach((q) => {
                    if (!q) return;
                    const qId = q.questionId !== undefined && q.questionId !== null ? q.questionId : q;
                    if (processedQuestionIds.has(qId)) return;
                    processedQuestionIds.add(qId);
                    this.getSelectedOptions(q).forEach((opt) => {
                        if (!opt?.tone) return;
                        const toneStr = String(opt.tone.tone || '').toLowerCase();
                        const color = opt.tone.color || null;
                        if (toneStr.includes('red') || opt.tone.isCritical) {
                            redCount++;
                            if (!criticalColor && color) criticalColor = color;
                        } else if (toneStr.includes('amber')) {
                            amberCount++;
                            if (!warningColor && color) warningColor = color;
                        } else if (toneStr.includes('green')) {
                            if (!greenColor && color) greenColor = color;
                        }
                    });
                });
            });
        });

        if (redCount > 0) {
            return {
                track: 'C',
                letterClass: 'text-danger',
                progressClass: 'bg-danger',
                bannerMessage: 'Critical items flagged — resolve before submitting to avoid Track C designation.',
                bannerType: 'red',
                flaggedCount: redCount,
                color: criticalColor || '#dc2626',
            };
        }
        if (amberCount > 0) {
            return {
                track: 'B',
                letterClass: 'text-warning',
                progressClass: 'bg-warning',
                bannerMessage: 'Non-critical items need review — address before submitting to avoid Track B designation.',
                bannerType: 'amber',
                flaggedCount: amberCount,
                color: warningColor || '#d97706',
            };
        }
        return { ...EMPTY_TRACK, color: greenColor || '#16a34a' };
    }

    getOverallTrackStatus(): TrackStatus {
        let redCount = 0;
        let amberCount = 0;
        let criticalColor: string | null = null;
        let warningColor: string | null = null;
        let greenColor: string | null = null;
        (this.salesrepChecklistDetails || []).forEach((checklist: any) => {
            if (!checklist?.checklistName) return;
            const status = this.getChecklistTrackStatus(checklist.checklistName);
            if (status.track === 'C') {
                redCount += status.flaggedCount;
                if (!criticalColor) criticalColor = status.color;
            } else if (status.track === 'B') {
                amberCount += status.flaggedCount;
                if (!warningColor) warningColor = status.color;
            } else if (!greenColor) {
                greenColor = status.color;
            }
        });
        if (redCount > 0) {
            return {
                track: 'C',
                letterClass: 'text-danger',
                progressClass: 'bg-danger',
                bannerMessage: 'Critical items flagged — resolve before submitting to avoid Track C designation.',
                bannerType: 'red',
                flaggedCount: redCount,
                color: criticalColor || '#dc2626',
            };
        }
        if (amberCount > 0) {
            return {
                track: 'B',
                letterClass: 'text-warning',
                progressClass: 'bg-warning',
                bannerMessage: 'Non-critical items need review — address before submitting to avoid Track B designation.',
                bannerType: 'amber',
                flaggedCount: amberCount,
                color: warningColor || '#d97706',
            };
        }
        return { ...EMPTY_TRACK, color: greenColor || '#16a34a' };
    }

    getQuestionAnswerText(q: any): string {
        if (!q) return '';
        const val = q.currentResponse !== undefined && q.currentResponse !== null ? q.currentResponse : q.answer;
        const savedAnswers = this.getSavedResponseAnswers(val);
        if (savedAnswers.length > 0) {
            return savedAnswers
                .map((answer: any) => answer.answerText || answer.label || answer.responseValue || answer.value || '')
                .filter(Boolean)
                .join(', ');
        }
        if (Array.isArray(val)) {
            return val
                .map((item: any) =>
                    typeof item === 'object' ? item.answerText || item.label || item.value || '' : String(item),
                )
                .filter(Boolean)
                .join(', ');
        }
        if (typeof val === 'object' && val) return val.answerText || val.label || val.value || '';
        return String(val || '');
    }

    private isPrefilledQuestionForSave(question: any): boolean {
        const isPrefilled = question?.isPrefilled === true || String(question?.isPrefilled).toLowerCase() === 'true';
        const prefilledFromQuestionId = question?.prefilledFromQuestionId;
        const hasPrefilledSource =
            prefilledFromQuestionId !== undefined &&
            prefilledFromQuestionId !== null &&
            prefilledFromQuestionId !== '' &&
            prefilledFromQuestionId !== 0 &&
            String(prefilledFromQuestionId).trim() !== '0';
        return isPrefilled || hasPrefilledSource;
    }

    private getCurrentChecklistName(): string | null {
        if (this.activeTab === 'summary') return 'summary';
        return (this.salesrepChecklistDetails || [])[this.selectedTabIndex]?.checklistName || null;
    }

    private getSectionIdentity(section: any): any {
        if (!section) return null;
        return section.sectionId !== undefined && section.sectionId !== null
            ? section.sectionId
            : section.sectionName || section.title || null;
    }

    private restoreChecklistViewState(viewState: ChecklistViewState): boolean {
        if (!viewState?.checklistName) return false;
        if (viewState.checklistName === 'summary') {
            this.selectedTabIndex = this.salesrepChecklistDetails.length;
            this.onTabChange(this.selectedTabIndex, false);
            return true;
        }
        const checklistIndex = this.getChecklistIndexByName(viewState.checklistName);
        if (checklistIndex < 0) return false;
        this.selectTab(checklistIndex, false);
        const availableProductTabs =
            viewState.checklistName === 'Contract Submission'
                ? this.getChecklistSubTabs(viewState.checklistName)
                : this.getActiveProducts();
        if (viewState.productTab && availableProductTabs.includes(viewState.productTab)) {
            this.activeProductTab = viewState.productTab;
            const expandedSectionIdSet = new Set((viewState.expandedSectionIds || []).map((id: any) => String(id)));
            (this.checklistDynamicTemplates[this.activeProductTab] || []).forEach((section: any) => {
                section.expanded = expandedSectionIdSet.has(String(this.getSectionIdentity(section)));
            });
        }
        return true;
    }

    private initializeTemplateSectionExpansion(productKey?: string): void {
        const templateKeys = productKey ? [productKey] : Object.keys(this.checklistDynamicTemplates);
        templateKeys.forEach((product: string) => {
            (this.checklistDynamicTemplates[product] || []).forEach((section: any) => {
                if (section.expanded === undefined) {
                    const isUtilitySection =
                        String(section.sectionName || section.title || '')
                            .trim()
                            .toLowerCase() === 'utility';
                    section.expanded = !this.isSectionComplete(section) && !isUtilitySection;
                }
            });
        });
    }

    private setDefaultProductTabForChecklist(checklistName: string): void {
        if (checklistName === 'Contract Submission') {
            const subTabs = this.getChecklistSubTabs(checklistName);
            if (!this.activeProductTab || !subTabs.includes(this.activeProductTab)) {
                this.activeProductTab = subTabs[0] || this.getActiveProducts()[0] || this.activeProductTab;
            }
            return;
        }
        const products = this.getActiveProducts();
        if (products.length && (!this.activeProductTab || !products.includes(this.activeProductTab))) {
            this.activeProductTab = products[0];
        }
    }

    private getActiveQuestionsForChecklist(checklistName: string): any[] {
        const templates = this.groupSectionsByProduct(checklistName);
        const tabs =
            checklistName === 'Contract Submission'
                ? this.getChecklistSubTabs(checklistName, templates)
                : this.getActiveProducts();
        const activeQuestions: any[] = [];
        tabs.forEach((tabKey: string) => {
            (templates[tabKey] || []).forEach((section: any) => {
                if (section.sectionId === 0) return;
                activeQuestions.push(...this.getAllActiveQuestionsForSection(section, tabKey));
            });
        });
        return activeQuestions;
    }

    private getActiveQuestionIdsOnSave(): any[] {
        return (this.salesrepChecklistDetails || []).reduce((ids: any[], checklist: any) => {
            if (!checklist?.checklistName) return ids;
            return ids.concat(
                this.getActiveQuestionsForChecklist(checklist.checklistName).map((question: any) => question?.questionId),
            );
        }, []);
    }

    private evaluateContractCreationCompletion(): void {
        if (!this.isContractCreationComplete() || this.hasAutoAdvancedToSubmission) return;
        this.hasAutoAdvancedToSubmission = true;
        const submissionIndex = this.getChecklistIndexByName('Contract Submission');
        if (submissionIndex >= 0) this.selectTab(submissionIndex, false);
    }

    private getSectionApplicableProducts(section: any): string[] {
        return Array.isArray(section?.applicableProducts) ? section.applicableProducts : [];
    }

    private getSectionApplicableStates(section: any): string[] {
        return Array.isArray(section?.applicableStates) ? section.applicableStates : [];
    }

    private isSectionApplicableToSelectedProducts(section: any): boolean {
        if (!section) return false;
        if (section.sectionId === 0) return true;
        const applicableProducts = this.getSectionApplicableProducts(section);
        if (!applicableProducts.length) return true;
        return applicableProducts.some(
            (product: string) => !!this.selectedProducts[product] || String(product).trim().toLowerCase() === 'shared',
        );
    }

    private isSectionApplicableToCurrentState(section: any): boolean {
        if (!section) return false;
        const applicableStates = this.getSectionApplicableStates(section);
        if (!applicableStates.length) return true;
        const opportunityState = String(this.tobarDetails?.state || '')
            .trim()
            .toLowerCase();
        return applicableStates.some(
            (state: string) => String(state || '').trim().toLowerCase() === opportunityState,
        );
    }

    private isSectionVisible(section: any): boolean {
        if (!section || section.sectionId === 0) return false;
        return this.isSectionApplicableToSelectedProducts(section) && this.isSectionApplicableToCurrentState(section);
    }

    private getChecklistSectionsForSave(checklist: any): any[] {
        if (!checklist?.sections) return [];
        return checklist.sections.filter((section: any) => this.isSectionVisible(section));
    }

    private applySavedProductSelection(): void {
        const q0 = this.getSectionZeroQuestion('Contract Creation');
        if (!q0) return;
        const selectedValues = this.getQuestionResponseValues(q0).map((value: any) => String(value));
        if (!selectedValues.length) return;
        Object.keys(this.selectedProducts).forEach((product: string) => {
            this.selectedProducts[product] = selectedValues.some((selectedValue: string) => selectedValue === product);
        });
        const activeProducts = this.getActiveProducts();
        if (activeProducts.length && (!this.activeProductTab || !activeProducts.includes(this.activeProductTab))) {
            this.activeProductTab = activeProducts[0];
        }
    }

    private isTextareaQuestion(question: any): boolean {
        const type = String(question?.questionType || '')
            .trim()
            .toLowerCase();
        return type === 'input-textarea' || type.includes('textarea');
    }

    private checkFinancePartnerCondition(question: any, productTabContext?: string): boolean {
        if (!question || question.financePartner === null || question.financePartner === undefined) return true;
        const requiredPartners = this.extractFinancePartnerValues(question.financePartner);
        if (requiredPartners.length === 0) return true;
        const currentProduct = productTabContext || this.activeProductTab;
        const selectedFinancePartners = this.getSelectedFinancePartnersForProduct(currentProduct);
        return requiredPartners.some((req: string) =>
            selectedFinancePartners.some((sel: string) => this.valuesMatch(req, sel)),
        );
    }

    private extractFinancePartnerValues(partnerObj: any): string[] {
        if (partnerObj === null || partnerObj === undefined) return [];
        if (typeof partnerObj === 'string' || typeof partnerObj === 'number') {
            const val = String(partnerObj).trim();
            return val ? [val] : [];
        }
        if (Array.isArray(partnerObj)) {
            return partnerObj.reduce(
                (acc: string[], item: any) => acc.concat(this.extractFinancePartnerValues(item)),
                [],
            );
        }
        if (typeof partnerObj === 'object') {
            const val =
                partnerObj.financePartnerName ||
                partnerObj.name ||
                partnerObj.partnerName ||
                partnerObj.value ||
                partnerObj.financePartner ||
                partnerObj.code ||
                partnerObj.label;
            if (val !== undefined && val !== null && val !== '') return [String(val).trim()];
        }
        return [];
    }

    getSelectedFinancePartnersForProduct(productName?: string): string[] {
        if (!productName || String(productName).trim().toLowerCase() === 'shared') {
            return this.getSelectedFinancePartnersFromChecklist();
        }

        const normProduct = String(productName).trim();
        const productPartners: string[] = [];

        // 1. Check OpportunityProduct in tobarDetails / context or global store
        const opportunityProducts =
            this.tobarDetails?.opportunityProduct ||
            this.tobarDetails?.OpportunityProduct ||
            this.tobarDetails?.opportunityProducts ||
            [];

        if (Array.isArray(opportunityProducts)) {
            opportunityProducts.forEach((op: any) => {
                const name = op?.product_Type_Name || op?.productTypeName || op?.productName || op?.product;
                if (this.valuesMatch(name, normProduct)) {
                    const partner =
                        op?.finance_Partner_Display_Name ||
                        op?.finance_Partner ||
                        op?.financePartnerName ||
                        op?.financePartner ||
                        op?.partner;
                    if (partner !== undefined && partner !== null && partner !== '') {
                        productPartners.push(String(partner).trim());
                    }
                }
            });
        }

        try {
            if (typeof window !== 'undefined') {
                const storeState = (useOneButtonStore as any)?.getState?.();
                const oppDetails = storeState?.opportunityDetails;
                const rawProducts = oppDetails?.OpportunityProduct ?? oppDetails?.opportunityProduct ?? [];
                if (Array.isArray(rawProducts)) {
                    rawProducts.forEach((op: any) => {
                        const name = op?.product_Type_Name || op?.productTypeName || op?.productName || op?.product;
                        if (this.valuesMatch(name, normProduct)) {
                            const partner =
                                op?.finance_Partner_Display_Name ||
                                op?.finance_Partner ||
                                op?.financePartnerName ||
                                op?.financePartner ||
                                op?.partner;
                            if (partner !== undefined && partner !== null && partner !== '') {
                                productPartners.push(String(partner).trim());
                            }
                        }
                    });
                }
            }
        } catch {
            // Ignore store access error if any
        }

        // 2. Check checklist questions for financepartner questions specific to this product
        const processQuestions = (questions: any[], inProductScope: boolean) => {
            if (!questions) return;
            questions.forEach((question: any) => {
                if (!question) return;
                const refEntity = String(question.referenceEntity || '')
                    .trim()
                    .toLowerCase();

                const questionApplicableProducts = Array.isArray(question.applicableProducts)
                    ? question.applicableProducts
                    : [];
                const questionMatchesProduct =
                    inProductScope ||
                    questionApplicableProducts.some((p: string) => this.valuesMatch(p, normProduct));

                if (refEntity === 'financepartner' && questionMatchesProduct) {
                    this.getSelectedOptions(question).forEach((opt: any) => {
                        this.getOptionCompareValues(opt).forEach((v: any) => {
                            if (v !== undefined && v !== null && v !== '') productPartners.push(String(v).trim());
                        });
                    });
                    this.getQuestionResponseValues(question).forEach((v: any) => {
                        if (v !== undefined && v !== null && v !== '') productPartners.push(String(v).trim());
                    });
                    const answerText = this.getQuestionAnswerText(question);
                    if (answerText) productPartners.push(String(answerText).trim());
                }

                (question.answerOptions || question.options || []).forEach((opt: any) => {
                    const optMatches = questionMatchesProduct || this.valuesMatch(opt?.answerText || opt?.text, normProduct);
                    if (opt?.childQuestions) processQuestions(opt.childQuestions, optMatches);
                });
                if (question.childQuestions) processQuestions(question.childQuestions, questionMatchesProduct);
            });
        };

        (this.salesrepChecklistDetails || []).forEach((checklist: any) => {
            (checklist.sections || []).forEach((sec: any) => {
                const secProducts = Array.isArray(sec?.applicableProducts) ? sec.applicableProducts : [];
                const secMatchesProduct =
                    secProducts.length === 0 || secProducts.some((p: string) => this.valuesMatch(p, normProduct));
                processQuestions(sec.questions || sec.questionsList || [], secMatchesProduct);
            });
        });

        const uniquePartners = Array.from(new Set(productPartners));
        if (uniquePartners.length > 0) {
            return uniquePartners;
        }

        return this.getSelectedFinancePartnersFromChecklist();
    }

    private getSelectedFinancePartnersFromChecklist(): string[] {
        const selectedPartners: string[] = [];
        const processQuestions = (questions: any[]) => {
            if (!questions) return;
            questions.forEach((question: any) => {
                if (!question) return;
                const refEntity = String(question.referenceEntity || '')
                    .trim()
                    .toLowerCase();
                if (refEntity === 'financepartner') {
                    this.getSelectedOptions(question).forEach((opt: any) => {
                        this.getOptionCompareValues(opt).forEach((v: any) => {
                            if (v !== undefined && v !== null && v !== '') selectedPartners.push(String(v).trim());
                        });
                    });
                    this.getQuestionResponseValues(question).forEach((v: any) => {
                        if (v !== undefined && v !== null && v !== '') selectedPartners.push(String(v).trim());
                    });
                    const answerText = this.getQuestionAnswerText(question);
                    if (answerText) selectedPartners.push(String(answerText).trim());
                }
                (question.answerOptions || question.options || []).forEach((opt: any) => {
                    if (opt?.childQuestions) processQuestions(opt.childQuestions);
                });
                if (question.childQuestions) processQuestions(question.childQuestions);
            });
        };
        (this.salesrepChecklistDetails || []).forEach((checklist: any) => {
            (checklist.sections || []).forEach((sec: any) => {
                processQuestions(sec.questions || sec.questionsList || []);
            });
        });
        return Array.from(new Set(selectedPartners));
    }

    private checkFiltersCondition(question: any): boolean {
        if (!question || !question.filters || typeof question.filters !== 'object') return true;
        const context = this.tobarDetails || {};
        const isWildcardOrEmpty = (val: any): boolean => {
            if (val === null || val === undefined) return true;
            const str = String(val).trim();
            return str === '' || str === '0';
        };
        const matchesValue = (filterVal: any, contextVal: any): boolean => {
            if (isWildcardOrEmpty(filterVal)) return true;
            if (contextVal === null || contextVal === undefined || String(contextVal).trim() === '') return false;
            if (Array.isArray(filterVal)) return filterVal.some((item: any) => this.valuesMatch(item, contextVal));
            return this.valuesMatch(filterVal, contextVal);
        };
        if ('state' in question.filters) {
            const stateMatch =
                matchesValue(question.filters.state, context.state) ||
                matchesValue(question.filters.state, context.stateCode);
            if (!stateMatch) return false;
        }
        if ('utilityCompany' in question.filters && !matchesValue(question.filters.utilityCompany, context.utilityCompany)) {
            return false;
        }
        if ('township' in question.filters && !matchesValue(question.filters.township, context.township)) {
            return false;
        }
        return true;
    }

    private checkParentAnswerOptionIdsCondition(question: any): boolean {
        if (
            !question ||
            !question.parentAnswerOptionIds ||
            !Array.isArray(question.parentAnswerOptionIds) ||
            question.parentAnswerOptionIds.length === 0
        ) {
            return true;
        }
        const requiredOptionIds = question.parentAnswerOptionIds.map((id: any) => String(id).trim());
        const selectedOptionIds = this.getAllSelectedAnswerOptionIds();
        return requiredOptionIds.some((reqId: string) => selectedOptionIds.has(reqId));
    }

    private getAllSelectedAnswerOptionIds(): Set<string> {
        const selectedIds = new Set<string>();
        const processQuestions = (questions: any[]) => {
            if (!questions) return;
            questions.forEach((question: any) => {
                if (!question) return;
                this.getSelectedOptions(question).forEach((opt: any) => {
                    if (opt && opt.answerOptionId !== undefined && opt.answerOptionId !== null) {
                        selectedIds.add(String(opt.answerOptionId).trim());
                    }
                });
                const val =
                    question.currentResponse !== undefined && question.currentResponse !== null
                        ? question.currentResponse
                        : question.answer;
                this.getSavedResponseAnswers(val).forEach((ans: any) => {
                    if (ans && ans.answerOptionId !== undefined && ans.answerOptionId !== null) {
                        selectedIds.add(String(ans.answerOptionId).trim());
                    }
                });
                (question.answerOptions || question.options || []).forEach((opt: any) => {
                    if (opt?.childQuestions) processQuestions(opt.childQuestions);
                });
                if (question.childQuestions) processQuestions(question.childQuestions);
            });
        };
        (this.salesrepChecklistDetails || []).forEach((checklist: any) => {
            (checklist.sections || []).forEach((sec: any) => {
                processQuestions(sec.questions || sec.questionsList || []);
            });
        });
        return selectedIds;
    }

    private findSectionForQuestion(question: any): any {
        if (!question) return null;
        const activeSections = this.checklistDynamicTemplates[this.activeProductTab] || [];
        for (const sec of activeSections) {
            const activeQs = this.getAllActiveQuestionsForSection(sec);
            if (activeQs.some((q) => q === question || q.questionId === question.questionId)) return sec;
        }
        return null;
    }

    private getSectionAutoCloseDelay(section: any): number {
        if (!section) return 600;
        if (section.autoCloseTime !== undefined && section.autoCloseTime !== null && Number(section.autoCloseTime) > 0) {
            return Number(section.autoCloseTime);
        }
        const activeQuestions = this.getAllActiveQuestionsForSection(section);
        const hasTextInputOrDate = activeQuestions.some((q) => {
            const type = String(q.questionType || '').toLowerCase();
            return type.includes('text') || type.includes('date') || type.includes('note') || type.includes('input');
        });
        if (hasTextInputOrDate) return 8000;
        const secName = String(section.sectionName || section.title || '').toLowerCase();
        const isMultiSelectSection =
            secName.includes('installation type') ||
            secName.includes('special work') ||
            secName.includes('combined packet');
        const hasMultiSelectQuestion = activeQuestions.some((q) => !!q.isMultiSelect);
        if (isMultiSelectSection || hasMultiSelectQuestion) return 8000;
        return 600;
    }

    private clearSectionTimer(secKey: any): void {
        if (this.sectionAutoCloseTimers.has(secKey)) {
            clearTimeout(this.sectionAutoCloseTimers.get(secKey));
            this.sectionAutoCloseTimers.delete(secKey);
        }
    }

    private handleSectionAutoCollapse(section: any): void {
        if (!section) return;
        const secKey = section.sectionId || section;
        this.clearSectionTimer(secKey);
        if (!this.isSectionComplete(section)) return;
        const delay = this.getSectionAutoCloseDelay(section);
        const timer = setTimeout(() => {
            if (this.isSectionComplete(section)) {
                section.expanded = false;
                const activeSections = this.checklistDynamicTemplates[this.activeProductTab] || [];
                const currentIndex = activeSections.findIndex(
                    (s: any) => (s.sectionId !== undefined && s.sectionId === section.sectionId) || s === section,
                );
                if (currentIndex !== -1 && currentIndex < activeSections.length - 1) {
                    const nextSection = activeSections[currentIndex + 1];
                    if (nextSection && !this.isSectionComplete(nextSection) && !nextSection.expanded) {
                        nextSection.expanded = true;
                    }
                }
                this.notify();
            }
            this.sectionAutoCloseTimers.delete(secKey);
        }, delay);
        this.sectionAutoCloseTimers.set(secKey, timer);
    }

    private hasSavedChildResponse(q: any): boolean {
        if (!q) return false;
        const resp = q.currentResponse;
        if (resp && typeof resp === 'object' && !Array.isArray(resp)) {
            if (resp.responseId !== undefined && resp.responseId !== null && resp.responseId !== 0) return true;
            if (resp.saved === true || String(resp.saved).toLowerCase() === 'true') return true;
            if (Array.isArray(resp.answers) && resp.answers.length > 0) return true;
        }
        return false;
    }

    private applyPrefilledQuestionValues(): void {
        if (!this.salesrepChecklistDetails || this.salesrepChecklistDetails.length === 0) return;
        const allQuestionsMap = new Map<any, any>();
        const collectQuestions = (qs: any[]) => {
            if (!qs) return;
            qs.forEach((q: any) => {
                if (q && q.questionId !== undefined && q.questionId !== null) {
                    allQuestionsMap.set(q.questionId, q);
                    allQuestionsMap.set(String(q.questionId), q);
                }
                (q.answerOptions || q.options || []).forEach((opt: any) => {
                    if (opt?.childQuestions?.length > 0) collectQuestions(opt.childQuestions);
                });
            });
        };
        this.salesrepChecklistDetails.forEach((checklist: any) => {
            (checklist.sections || []).forEach((sec: any) => {
                collectQuestions(sec.questions || sec.questionsList || []);
            });
        });

        allQuestionsMap.forEach((q: any) => {
            const sourceQId = q.prefilledFromQuestionId;
            if (
                sourceQId === undefined ||
                sourceQId === null ||
                sourceQId === '' ||
                sourceQId === 0 ||
                String(sourceQId).trim() === '0'
            ) {
                return;
            }
            const sourceQ =
                allQuestionsMap.get(sourceQId) || allQuestionsMap.get(Number(sourceQId)) || allQuestionsMap.get(String(sourceQId));
            if (!sourceQ) return;
            const currParentAnswerTexts = this.getPrefillSourceAnswerTexts(sourceQ);
            const prevParentAnswerTexts =
                this.previousParentAnswersMap.get(sourceQId) ||
                this.previousParentAnswersMap.get(String(sourceQId)) ||
                this.previousParentAnswersMap.get(Number(sourceQId));
            const isTargetMultiSelect = q?.isMultiSelect === true || String(q?.isMultiSelect).toLowerCase() === 'true';
            const existingChildValues = this.getQuestionResponseValues(q);
            const hasSaved = this.hasSavedChildResponse(q);

            if (!hasSaved && existingChildValues.length === 0) {
                if (currParentAnswerTexts.length > 0) {
                    const parentDerivedValues = this.mapParentValuesToChildOptionValues(q, currParentAnswerTexts);
                    if (parentDerivedValues.length > 0) {
                        const nextResponse = isTargetMultiSelect ? parentDerivedValues : parentDerivedValues[0];
                        if (!this.areResponseValuesEqual(q.currentResponse, nextResponse)) {
                            q.currentResponse = nextResponse;
                            q.answer = nextResponse;
                        }
                    }
                }
            } else if (prevParentAnswerTexts !== undefined && sourceQ.isUnsaved) {
                const addedParentTexts = currParentAnswerTexts.filter(
                    (cText) => !prevParentAnswerTexts.some((pText) => this.valuesMatch(pText, cText)),
                );
                const removedParentTexts = prevParentAnswerTexts.filter(
                    (pText) => !currParentAnswerTexts.some((cText) => this.valuesMatch(cText, pText)),
                );
                if ((addedParentTexts.length > 0 || removedParentTexts.length > 0) && isTargetMultiSelect && !q.userOverridden) {
                    const addedChildValues = this.mapParentValuesToChildOptionValues(q, addedParentTexts);
                    const removedChildValues = this.mapParentValuesToChildOptionValues(q, removedParentTexts);
                    let updatedValues = [...existingChildValues];
                    if (removedChildValues.length > 0) {
                        updatedValues = updatedValues.filter(
                            (cVal: any) => !removedChildValues.some((rVal: any) => this.valuesMatch(cVal, rVal)),
                        );
                    }
                    addedChildValues.forEach((aVal: any) => {
                        if (!updatedValues.some((cVal: any) => this.valuesMatch(cVal, aVal))) updatedValues.push(aVal);
                    });
                    if (!this.areResponseValuesEqual(q.currentResponse, updatedValues)) {
                        q.currentResponse = updatedValues;
                        q.answer = updatedValues;
                        q.isUnsaved = true;
                    }
                }
            }
        });

        allQuestionsMap.forEach((q: any, qId: any) => {
            const parentAnswerTexts = this.getPrefillSourceAnswerTexts(q);
            this.previousParentAnswersMap.set(qId, parentAnswerTexts);
            this.previousParentAnswersMap.set(String(qId), parentAnswerTexts);
        });
    }

    private mapParentValuesToChildOptionValues(q: any, parentValues: string[]): any[] {
        if (!q || !parentValues || parentValues.length === 0) return [];
        const childOptions = q.answerOptions || q.options || [];
        if (childOptions.length === 0) return parentValues;
        const matchedChildOptions = childOptions.filter((opt: any) => {
            const childCompareValues = this.getOptionCompareValues(opt);
            return parentValues.some((parentAnswerText: string) =>
                childCompareValues.some((childCompareValue: any) => this.valuesMatch(childCompareValue, parentAnswerText)),
            );
        });
        return matchedChildOptions.map((opt: any) =>
            typeof opt === 'string' ? opt : opt.value !== undefined ? opt.value : opt.answerText,
        );
    }

    private getPrefillSourceAnswerTexts(sourceQuestion: any): string[] {
        const selectedTexts = this.getSelectedOptions(sourceQuestion).map((opt: any) =>
            typeof opt === 'string' ? opt : opt.answerText || '',
        );
        const responseTexts = this.getQuestionResponseValues(sourceQuestion).map((value: any) =>
            typeof value === 'object' ? value.answerText || '' : String(value),
        );
        return Array.from(
            new Set(
                [...selectedTexts, ...responseTexts]
                    .map((value: any) => String(value || '').trim())
                    .filter(Boolean),
            ),
        );
    }

    private areResponseValuesEqual(left: any, right: any): boolean {
        const normalizeValues = (value: any): string[] =>
            this.getResponseValues(value)
                .map((item: any) => String(item).trim().toLowerCase())
                .filter(Boolean)
                .sort();
        const leftValues = normalizeValues(left);
        const rightValues = normalizeValues(right);
        return leftValues.length === rightValues.length && leftValues.every((value, index) => value === rightValues[index]);
    }

    private syncProductSelectorQuestionSelection(checklistName: string): void {
        const checklist = this.getChecklistDetailsByName(checklistName);
        const section = checklist?.sections?.find((item: any) => item.sectionId === 0);
        const questions = section?.questions || section?.questionsList || [];
        const productQuestion = questions.find((q: any) =>
            (q.questionText || q.text || '').toLowerCase().includes('product'),
        );
        if (!productQuestion) return;
        const selectedValues = this.getActiveProducts().map((product: string) => {
            const matchingOption = (productQuestion.answerOptions || productQuestion.options || []).find((opt: any) => {
                const optText = typeof opt === 'string' ? opt : opt.answerText || opt.label || opt.value || '';
                return String(optText).trim().toLowerCase() === String(product).trim().toLowerCase();
            });
            if (matchingOption) {
                return typeof matchingOption === 'string'
                    ? matchingOption
                    : matchingOption.value !== undefined
                      ? matchingOption.value
                      : matchingOption.answerText || matchingOption.label;
            }
            return product;
        });
        productQuestion.currentResponse = selectedValues.length > 0 ? selectedValues : undefined;
        productQuestion.answer = productQuestion.currentResponse;
        productQuestion.isUnsaved = true;
        productQuestion.DateTimeStamp = new Date().toISOString();
        productQuestion.ModifiedUserFullName = this.userFullName;
    }

    private getProductSelectionValidationMessage(product: string): string | null {
        const isSelecting = !this.selectedProducts[product];
        if (!isSelecting) return null;
        if (product === 'RR' && this.getActiveProducts().length > 0) {
            return 'RR must be sold by itself — deselect other products first.';
        }
        if (this.selectedProducts['RR']) {
            return 'RR must be sold by itself — deselect other products first.';
        }
        if ((product === 'Roof' && this.selectedProducts['RRR']) || (product === 'RRR' && this.selectedProducts['Roof'])) {
            return 'Roof and RRR cannot be on the same opportunity.';
        }
        return null;
    }

    private isSectionZeroQuestion(question: any): boolean {
        return (this.salesrepChecklistDetails || []).some(
            (checklist: any) => this.getSectionZeroQuestion(checklist.checklistName) === question,
        );
    }

    private updateSaveButtonState(): void {
        this.unsavedChangesCount = this.getUnsavedAnsweredQuestions().length;
        this.saveDisabled = this.unsavedChangesCount === 0;
    }

    captureInitialResponses(): void {
        const processQuestions = (questions: any[]) => {
            if (!questions) return;
            questions.forEach((q: any) => {
                if (!q) return;
                const val =
                    q.currentResponse !== undefined && q.currentResponse !== null
                        ? q.currentResponse
                        : q.answer;
                q.initialResponse = val !== undefined && val !== null ? JSON.parse(JSON.stringify(val)) : undefined;
                q.isUnsaved = false;
                (q.answerOptions || q.options || []).forEach((opt: any) => {
                    if (opt?.childQuestions) processQuestions(opt.childQuestions);
                });
                if (q.childQuestions) processQuestions(q.childQuestions);
            });
        };
        (this.salesrepChecklistDetails || []).forEach((checklist: any) => {
            (checklist.sections || []).forEach((sec: any) => {
                processQuestions(sec.questions || sec.questionsList || []);
            });
        });
    }
}
